import type { Text } from '@kkf/design'

/**
 * Content for the about page, drawn from `docs/resources/Case for Support` — the need,
 * our response, our story and the four phases of expected outcomes.
 *
 * **The capital ask is not here any more (2026-09-19).** `ABOUT_BUDGET` rendered the USD
 * $1 million requirement as a budget table with the founder's signature under it, two
 * thirds of the way down a page about who the Foundation is. An ask belongs where somebody
 * can act on it, so it moved to `/donations` as its own appeal — `initial-capital-investment`
 * in `data/donations`, which carries the same figures, the same two closing paragraphs and
 * a give action. Do not re-add it here; edit it there.
 */
export const ABOUT = {
  images: {
    /**
     * The Foundation's full mark, not a photograph (2026-09-21).
     *
     * The slot held `about-img.png` — a 690x612 picture from the purchased template — on
     * the one page whose job is to say who this organisation is. The mark says it, and it
     * is the only page on the platform where the strapline belongs at size: the header's
     * lockup drops it because at ~40px it is a grey smudge.
     *
     * `logo-foundation-full.svg` is generated by `tools/logo/build-lockups.py` beside the
     * lockups, so the lion and the wordmark here cannot
     * drift from the ones in the header and the footer. It is vector, so the 690x612 the
     * template sized this slot for is no longer a ceiling.
     */
    main: '/assets/img/logo-foundation-full.svg',
    mainAlt: {
      en: 'King of Kings Foundation — the crowned lion, the wordmark, and the line “For lives worth living”',
      mn: 'King of Kings Foundation — титэмтэй арслан, нэрийн тэмдэг, “Үнэ цэнтэй амьдралын төлөө” гэсэн мөр',
    } satisfies Text,
    /**
     * The banner photograph — the resort campus from the air. The only real image on this
     * page; everything else is still a sized placeholder.
     */
    banner: '/assets/img/resort-campus-aerial.jpg',
    bannerAlt:
      'The resort campus from the air — guest cottages and a domed main hall set among ' +
      'lawns, gardens and a playground, ringed by pine forest and mountains',
    vector1: '/assets/img/about-img-vector-1.svg',
    vector2: '/assets/img/about-img-vector-2.svg',
    sectionVector: '/assets/img/about-vector-1.png',
  },
  subTitle: { en: 'About US', mn: 'Бидний тухай' },
  title: { en: 'For Lives Worth Living', mn: 'Үнэ цэнтэй амьдралын төлөө' },
  descr:
    { en: 'King of Kings Foundation exists to accompany people on their journey toward a closer relationship with God and to facilitate them in living Christ-centered, abundant lives. We do this by inspiring and equipping people through discipleship, mentoring, leadership partnerships, and a Christ-centered resort that offers rest, connection, and holistic care. (John 10:10)', mn: 'King of Kings Foundation нь хүмүүсийг Бурхантай илүү дотно харилцаанд хүрэх замд нь дагалдан явж, Христэд төвлөрсөн, элбэг дэлбэг амьдралаар амьдрахад нь туслахын тулд оршдог. Бид үүнийг шавь бэлтгэх, зөвлөх, манлайллын түншлэл, мөн амралт, холбоо, бүрэн хүртээмжтэй халамжийг санал болгодог Христэд төвлөрсөн амралтын газраар дамжуулан хүмүүсийг урамшуулж, чадавхжуулан хийдэг. (Иохан 10:10)' },
  /**
   * What replaced "The Need" (2026-09-21).
   *
   * That block was four bullets of what is wrong in Mongolia — broken relationships, few
   * role models, limited opportunity, a search for hope — under a heading reading "The
   * Need", with a photograph beside it and, under both, a button to the resort and the
   * Foundation's inbox at heading size.
   *
   * The Foundation asked for what it *believes* in that position instead. The page's first
   * screen is now what this organisation is for rather than what is wrong with the place it
   * works in, which is a different argument and a better one to open on.
   *
   * The button and the inbox went with it. Neither was part of the replacement, and both
   * are still one click away — `/contact` carries the inbox, and the resort is in the nav,
   * the footer and the off-canvas menu on every page.
   */
  belief: {
    title: { en: 'Our Belief', mn: 'Бидний итгэл' } satisfies Text,
    body: {
      en: 'We believe God has a unique purpose for your life — one that beautifully aligns with your personality, gifts, and passions. True fulfillment and meaningful life begins when you step into that calling.',
      mn: 'Бид Бурхан таны амьдралд өвөрмөц зорилго бэлдсэн гэдэгт итгэдэг — таны зан чанар, авьяас, хүсэл тэмүүлэлтэй гайхалтай нийцэх зорилго. Жинхэнэ ханамж, утга учиртай амьдрал тэрхүү дуудлага руугаа алхах үед эхэлдэг.',
    } satisfies Text,
  },
  heart: {
    title: { en: 'Our Heart', mn: 'Бидний зүрх сэтгэл' } satisfies Text,
    paragraphs: [
      {
        en: 'At KKF, we are here no matter where your journey starts. Whether you are navigating deep darkness, simply surviving day by day, or striving for a better future but lacking the right support — we can be your resting spot and guide.',
        mn: 'KKF-д бид таны аялал хаанаас ч эхэлсэн байсан хамаагүй энд байна. Та гүн харанхуйг туулж байгаа ч бай, өдөр бүрийг арай ядан өнгөрүүлж байгаа ч бай, эсвэл илүү сайн ирээдүйн төлөө тэмүүлж байгаа боловч зохих дэмжлэг дутагдаж байгаа ч бай — бид таны амрах газар, хөтөч байж чадна.',
      },
      {
        en: 'We are here to partner with you, equip you, and provide a true place of rest. Together, as a community of like-minded hearts, we want to help you build a joyful, impactful life. A life that, when you look back years from now, was deeply worth living.',
        mn: 'Бид тантай хамтран ажиллаж, чадавхжуулж, жинхэнэ амрах газар олгохоор энд байна. Ижил бодолтой хүмүүсийн хамт олон болж, бид танд баяр хөөртэй, нөлөө бүхий амьдрал бүтээхэд туслахыг хүсэж байна. Олон жилийн дараа эргэн харахад үнэхээр амьдрах нь зүйтэй байсан амьдрал.',
      },
    ] satisfies Text[],
  },
  /**
   * The three pillars, as a list with the icons the removed block's bullets used.
   *
   * Written as they were given, with one correction: the source read "the uniquq calling".
   * A typo in supplied copy is corrected silently; anything that changes the meaning is
   * not, and this does not.
   */
  pillars: {
    title: { en: 'Core Pillars of KKF', mn: 'KKF-ийн үндсэн тулгуурууд' } satisfies Text,
    items: [
      {
        name: { en: 'Purpose', mn: 'Зорилго' } satisfies Text,
        descr: {
          en: 'We work to create space for you to discover the unique calling and role God has designed just for you.',
          mn: 'Бурхан яг танд зориулж бүтээсэн өвөрмөц дуудлага, үүргээ нээхэд тань зориулж орон зай бий болгохоор бид ажилладаг.',
        } satisfies Text,
      },
      {
        name: { en: 'Community', mn: 'Хамт олон' } satisfies Text,
        descr: {
          en: 'We meet you exactly where you are, offering unwavering support through every season.',
          mn: 'Бид таныг яг байгаа газарт нь угтан авч, улирал бүрд тань ганхашгүй дэмжлэг үзүүлдэг.',
        } satisfies Text,
      },
      {
        name: { en: 'Impact', mn: 'Нөлөө' } satisfies Text,
        descr: {
          en: 'We work to assist you to live a joyful, fulfilling life that leaves a lasting mark on the world and the Kingdom of God.',
          mn: 'Дэлхий ертөнц болон Бурханы хаанчлалд мөнхийн ул мөр үлдээх баяр хөөртэй, ханамжтай амьдралаар амьдрахад тань туслахаар бид ажилладаг.',
        } satisfies Text,
      },
    ],
  },
}

/**
 * What the Foundation is, in four short phrases — and, on one of them, a figure.
 *
 * **Three of the four stopped being counts on 2026-09-21.** They were 7 areas of work,
 * 4 phases of impact and 3 social enterprises: real counts, but counts of the page's own
 * structure rather than of anything a reader came for. The Foundation asked for the three
 * commitments instead, and a commitment has no number — so `number` is unset on them and
 * the circle closes up, rather than a figure being found to fill the slot. `StatItem`'s own
 * comment has the rule.
 *
 * The fourth keeps its word because "Nationwide" is a statement about the Partners board
 * and not a count. The template shipped "260+ Happy Children" and similar; nobody has
 * supplied an audience figure and none is invented here.
 *
 * `StatItem` is the design system's and takes plain strings. This is the data, so the
 * page maps it — the same seam `ServiceEntry` crosses above.
 */
export const STATS: { icon: string; number?: string; txt: Text }[] = [
  { icon: 'costumer', txt: { en: 'Christ Centred Community', mn: 'Христэд төвлөрсөн хамт олон' } },
  { icon: 'team', txt: { en: 'Honouring Sabbath Rest', mn: 'Амралтын өдрийг хүндэтгэх' } },
  { icon: 'package', txt: { en: 'Gospel Through Genuine Relationship', mn: 'Жинхэнэ харилцаагаар дамжсан Сайн мэдээ' } },
  { icon: 'relationship', number: 'Nationwide', txt: { en: 'Christian Business Platform', mn: 'Христийн бизнесийн платформ' } },
]

/**
 * The four panels, in a two-up grid: **Mission, Vision, Story, What We Do.**
 *
 * **The order is about length, not about importance (2026-09-21).** It was Story, Mission,
 * Vision, and the story is four paragraphs against the vision's two sentences — so the
 * first row was a tall card beside a short one and the second row was the reverse. Pairing
 * the two short panels and then the two long ones makes each row roughly the height of its
 * own content, which is what stops a card being mostly the white space `height: 100%` gives
 * it to match its neighbour.
 *
 * Each panel has a different shape — the story is prose, What We Do carries the seven areas
 * of our work between two paragraphs, the vision is the statement and its verse — which is
 * why they are composed in the page rather than generated from one shape. **The page keys
 * off each panel's `id` rather than its position**, so reordering them here moves them
 * without breaking what renders inside, which is exactly what this change did.
 */
export const ABOUT_TABS = {
  // The heading covers all three panels, so it cannot be "Our response" any more — that
  // introduced the mission, which no longer leads.
  /*
   * **No eyebrow (2026-09-21).** It read "Who we are" and was invisible: this heading sets
   * its title `text-white` for the dark band behind it, and `.ul-section-sub-title` takes
   * `--ul-secondary` from the shared rule — the working blue, on near-black. Nothing
   * failed, because a contrast ratio is the one thing no gate here can see.
   *
   * Removed rather than recoloured, because the title already says what the section is and
   * the eyebrow was saying it a second time. `SectionHeading` emits no span at all when
   * `subTitle` is absent — see its own header for why that had to be made true.
   */
  heading: {
    title: { en: 'Our Mission, Our Vision, Our Story and What We Do', mn: 'Бидний эрхэм зорилго, алсын хараа, түүх, үйл ажиллагаа' },
    ctaLabel: { en: 'Donate Now', mn: 'Одоо хандивлах' },
    ctaTo: '/donations',
  },
  panels: [
    {
      id: 'mission',
      image: '/assets/img/about-family-steppe.jpg',
      imageAlt:
        'A family of nine in traditional dress standing together outside their home on ' +
        'the steppe, hills behind them',
      title: { en: 'Our Mission', mn: 'Бидний эрхэм зорилго' },
      // The statement and nothing else. "We respond through…", the seven areas and the
      // closing paragraph were under it until 2026-09-21; they are the `work` panel now.
      descr:
        { en: 'King of Kings Foundation exists to accompany people on their journey toward a closer relationship with God and to facilitate them in living Christ-centered, abundant lives. We do this by inspiring and equipping people through discipleship, mentoring, leadership partnerships, and a Christ-centered resort that offers rest, connection, and holistic care. (John 10:10)', mn: 'King of Kings Foundation нь хүмүүсийг Бурхантай илүү дотно харилцаанд хүрэх замд нь дагалдан явж, Христэд төвлөрсөн, элбэг дэлбэг амьдралаар амьдрахад нь туслахын тулд оршдог. Бид үүнийг шавь бэлтгэх, зөвлөх, манлайллын түншлэл, мөн амралт, холбоо, бүрэн хүртээмжтэй халамжийг санал болгодог Христэд төвлөрсөн амралтын газраар дамжуулан хүмүүсийг урамшуулж, чадавхжуулан хийдэг. (Иохан 10:10)' },
    },
    {
      id: 'vision',
      image: '/assets/img/about-ger-steppe.jpg',
      imageAlt: { en: 'A white ger with a solar panel beside it, standing alone on open green grassland', mn: 'Нарны хавтантай цагаан гэр, уудам ногоон бэлчээрт ганцаараа' },
      title: { en: 'Our Vision', mn: 'Бидний алсын хараа' },
      descr:
        { en: 'Our vision is to see people healed, restored, and set free through Christ, and to equip them to rise as servant leaders who transform their communities for the Kingdom of God. (Isaiah 61:1)', mn: 'Бидний алсын хараа бол Христээр дамжин хүмүүс эдгэрч, сэргэж, чөлөөлөгдөхийг харах, мөн тэднийг Бурханы хаанчлалын төлөө хамт олноо өөрчлөх үйлчлэгч манлайлагч болоход чадавхжуулахад оршино. (Исаиа 61:1)' },
    },
    {
      id: 'story',
      image: '/assets/img/about-lodge-snow.jpg',
      imageAlt: { en: 'A timber lodge under fresh snow, backed by a snow-covered pine forest', mn: 'Шинэ цасан доорх модон байшин, ард нь цасанд дарагдсан нарс ой' },
      title: { en: 'Our Story', mn: 'Бидний түүх' },
      descr:
        { en: 'Throughout all our work, we seek to inspire people to discover their God-given calling, experience the freedom found in Christ, and become leaders of their families and communities. This calling is deeply personal.', mn: 'Бүх ажилдаа бид хүмүүсийг Бурханаас өгөгдсөн дуудлагаа нээж, Христ дотор олдох эрх чөлөөг мэдэрч, гэр бүл, хамт олныхоо манлайлагч болоход урамшуулахыг эрмэлздэг. Энэ дуудлага гүн хувийн шинжтэй.' },
      second:
        'A board member who grew up in a single-parent family experienced God’s faithfulness in building a strong Christ-centered family, and now mentors women, strengthens marriages, and helps young people embrace their God-given gender identity. Another member mentors men through sports and technology, building relationships with those far from God and walking alongside them through life’s most difficult seasons, reminding them God still cares.',
      third:
        'Founded by an interracial couple, our board also includes leaders who have led multinational Christian organizations and businesses across several countries, bringing extensive experience in leadership, cross-cultural collaboration, and developing people.',
      fourth:
        'Through arts, holistic care, and mentoring, we have also seen people find hope, discover their callings, pursue education, overcome suicidal seasons, and build stronger families.',
    },
    {
      /**
       * **A fourth panel, split out of the mission on 2026-09-21.**
       *
       * The mission panel carried the statement, then "we respond through…", then the seven
       * areas of our work, then a closing paragraph about what the revenue pays for. It was
       * four things in the panel whose job is to say one, and it was the longest thing on
       * the page by some distance.
       *
       * So the mission is the mission now, and the seven areas are their own card. Nothing
       * was cut: `response`, `missionList` and `closing` are the same words, under a
       * heading that says what they are.
       *
       * It had the full row for an hour on the strength of its seven-item list, then sat
       * beside the vision, and it is last now — beside the story, which is the other long
       * one. Four panels of equal weight need no layout rule at all; the order is the
       * layout, and the order is about length. See the header of `ABOUT_TABS`.
       */
      id: 'work',
      /**
       * A real photograph, not the template's sized grey placeholder.
       *
       * The other three panels on this page carry real ones, and a panel listing seven
       * things the Foundation actually does is the last place to print "690X700" over the
       * top of them. `blog-bat-mentor-workshop.jpg` is the mentoring the first item names.
       */
      image: '/assets/img/blog-bat-mentor-workshop.jpg',
      imageAlt: {
        en: 'An older tradesman and a young apprentice with a drill, in a house they are framing',
        mn: 'Барьж буй байшиндаа өрөмтэй зогсох туршлагатай мастер, залуу дагалдан',
      } satisfies Text,
      title: { en: 'What We Do', mn: 'Бид юу хийдэг вэ' },
      descr:
        { en: 'We respond through discipleship, mentorship, practical education, hospitality, and community development, creating opportunities to connect people with God, meaningful work, and one another. Our work includes:', mn: 'Бид шавь бэлтгэх, зөвлөх, практик боловсрол, зочломтгой байдал, хамт олны хөгжлөөр дамжуулан хариу үйлдэл үзүүлж, хүмүүсийг Бурхантай, утга учиртай ажилтай, бие биетэйгээ холбох боломж бүрдүүлдэг. Бидний ажилд:' },
      closing:
        'The ministry addresses these needs through mentoring, practical skills, spiritual guidance, meaningful work, and genuine community. Revenue and donations from the businesses support youth development and help provide churches, organizations, and expatriates with accessible opportunities for rest, retreats, camps, and community events at the resort, including free programs.',
    },
  ],
  /**
   * The seven areas of our work, on the **What We Do** panel. They were the mission
   * panel's until 2026-09-21; the name is kept so `git log` on this file reads as one
   * thread rather than as a list that disappeared and a different one that appeared.
   */
  missionList: [
    'Youth mentoring and skills: automotive work, trades, robotics, technology, creative arts and life skills, alongside spiritual and personal guidance.',
    'Christ-centered resort: rest, recreation, spiritual growth, retreats, camps and community events.',
    'Missionary tourism and short-term missions: customizable tours that serve alongside local churches and organizations.',
    'Marriage and family: free or supported weddings, premarital preparation and family mentoring.',
    'Christian business platform: a nationwide website and app connecting customers with Christian businesses and services.',
    'Holistic health initiatives: natural foods, medicinal herbs, healthy living and practical wellness education.',
    'Community and wellness: spaces and programs for churches, organizations, expatriates and communities.',
  ],
  /*
   * `visionList` was here and is gone (2026-09-21), at the Foundation's request, with the
   * sentence that introduced it: "Our goal is not simply to measure how many people come
   * through our doors, but:" followed by four measures — lives strengthened, relationships
   * built, opportunities created, communities connected.
   *
   * The vision panel is the vision statement and Isaiah 61:1 now. `git log -- this file`
   * has the four if they are ever wanted back.
   */
  vectorImage: '/assets/img/events-vector-2.svg',
}

/*
 * `AboutPhase`, `ABOUT_PHASES_HEADING` and `ABOUT_PHASES` were here, and they are gone
 * (2026-09-21).
 *
 * They rendered "Expected outcomes — How We Will Know It Is Working": four numbered cards
 * carrying the case for support's phases, from Foundation through to Sustainability &
 * Multiplication. It was removed at the Foundation's request, so the whole block goes
 * rather than being hidden behind a flag — a section nothing renders is a section nobody
 * can tell is deliberate.
 *
 * `.ul-phase` went with it: `sections/_impact.scss` had no other consumer and dead rules in
 * a shared stylesheet are worse than dead code, because nothing typechecks them and the
 * next person assumes a page still uses them. That is the same argument the file itself
 * made when `.ul-budget` came out of it.
 *
 * `git log -- app_website/src/data/about.ts` has the four phases and their Mongolian if
 * they are ever wanted back.
 */

export const ABOUT_TEAM_HEADING = {
  subTitle: { en: 'Our Team', mn: 'Бидний баг' },
  title: { en: 'The People Who Walk Alongside', mn: 'Хамт алхдаг хүмүүс' },
  ctaLabel: { en: 'Join With us', mn: 'Бидэнтэй нэгдээрэй' },
  ctaTo: '/team',
}

/*
 * `CTA` and `TESTIMONIAL` used to live here and were removed on 2026-09-02 with the home
 * page's rework — this was their only consumer.
 *
 * `CTA` ("Help Us Build A Place Of Hope In Mongolia" → `/contact`) was replaced by
 * `HOME_PARTNERS_CTA` in `data/home`, which made Partners' concrete 5% offer instead
 * of a general one — and that band came off the home page in turn on 2026-09-19. The offer
 * is now made on `/donations`, at the top of a page somebody opened in order to act.
 * `TESTIMONIAL` was the template's lorem ipsum under an invented reviewer named
 * "Esther Howard", with a fabricated 4.9 rating; nothing about it was ours to publish.
 * When there are real testimonies, see the note in `app_design/src/styles/index.scss`.
 */
